import { useEffect, useRef } from 'react';
import { App } from '@capacitor/app';

/**
 * Roept `onResume` aan zodra de app weer op de voorgrond komt.
 *
 * Een native app blijft dagen open staan; zonder dit staat er bij het openen langs de
 * lijn nog de opstelling van vorige week. Meteen verversen houdt bovendien de
 * offline-cache warm voor het moment dat het netwerk wegvalt.
 *
 * Op het web vuurt appStateChange niet, dus daar is dit een no-op.
 */
export const useAppResume = (onResume: () => void): void => {
  // Via een ref, zodat de aanroeper zijn callback niet in useCallback hoeft te wikkelen
  // en de listener niet bij elke render opnieuw wordt aangemeld.
  const callbackRef = useRef(onResume);
  callbackRef.current = onResume;

  useEffect(() => {
    let active = true;
    let removeListener: (() => void) | undefined;

    App.addListener('appStateChange', ({ isActive }) => {
      if (isActive && active) callbackRef.current();
    })
      .then((handle) => {
        if (active) {
          removeListener = () => handle.remove();
        } else {
          handle.remove();
        }
      })
      .catch(() => {
        // Geen plugin (web of tests): dan is er niets om op te wachten.
      });

    return () => {
      active = false;
      removeListener?.();
    };
  }, []);
};
